import { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '@/App';
import { getMyAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement, getMyCourses } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Megaphone, Pencil, Trash2 } from 'lucide-react';

export default function InstructorAnnouncements() {
  const { currentInstructor } = useAppContext();
  const [announcements, setAnnouncements] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', course_id: '' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [aRes, cRes] = await Promise.all([getMyAnnouncements(), getMyCourses()]);
      const myCourses = cRes.data || [];
      setCourses(myCourses);
      const myCourseIds = myCourses.map(c => c.id);
      const myAnnouncements = (aRes.data || []).filter(a =>
        !a.course_id || myCourseIds.includes(a.course_id)
      );
      setAnnouncements(myAnnouncements);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (currentInstructor?.id) loadData();
  }, [currentInstructor?.id, loadData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', content: '', course_id: courses.length > 0 ? courses[0].id : '' });
    setShowDialog(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, content: a.content, course_id: a.course_id || '' });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Title and content required'); return; }
    try {
      const payload = { ...form, author: currentInstructor?.name || 'Instructor' };
      if (editing) {
        await updateAnnouncement(editing.id, payload);
        toast.success('Updated');
      } else {
        await createAnnouncement(payload);
        toast.success('Posted');
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try { await deleteAnnouncement(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); }
  };

  const courseMap = {};
  courses.forEach(c => { courseMap[c.id] = c.title; });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>My Announcements</h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">Create and manage announcements for your courses</p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10] btn-hover" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" /> New Announcement
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />)}</div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-16">
          <Megaphone className="w-12 h-12 text-[#062B1A]/15 mx-auto mb-3" />
          <p className="text-[#062B1A]/50 mb-3">No announcements yet</p>
          <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={openCreate}>Post Your First Announcement</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(ann => (
            <Card key={ann.id} className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Megaphone className="w-4 h-4 text-[#D4AF37]" />
                      <h3 className="text-sm font-semibold text-[#062B1A]">{ann.title}</h3>
                      {ann.course_id && <Badge variant="outline" className="text-[10px]">{courseMap[ann.course_id] || 'Course'}</Badge>}
                    </div>
                    <p className="text-sm text-[#062B1A]/60 mb-1">{ann.content}</p>
                    <p className="text-xs text-[#062B1A]/40">{new Date(ann.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(ann)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(ann.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-display)' }}>{editing ? 'Edit' : 'New'} Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Title *</label>
              <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="border-[rgba(6, 43, 26,0.12)]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Content *</label>
              <Textarea value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} className="min-h-[100px] border-[rgba(6, 43, 26,0.12)]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Course</label>
              <Select value={form.course_id} onValueChange={v => setForm(f => ({...f, course_id: v}))}>
                <SelectTrigger className="border-[rgba(6, 43, 26,0.12)]"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>
                  {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleSave}>{editing ? 'Update' : 'Post'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
